/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Range, Root, Thumb, Track } from "@radix-ui/react-slider";
import type { ComponentProps } from "react";

import {
  colors,
  constantSemanticColors,
  numericPercentages,
  numericPixels,
  radius,
  shades,
  spacing,
  stroke,
  useEmotionColor,
} from "../../../../../global/emotion";
import type { Color } from "../../../../types/color.emotion.types";

export type SliderProps = Omit<
  ComponentProps<typeof Root>,
  "value" | "defaultValue"
> & {
  value?: number;
  defaultValue?: number;
  size?: "small" | "large" | "xl";
  onColor?: boolean;
  color?: Color;
  css?: Interpolation;
  /** Adds the keyboard-operable thumb, named by this string; omit it for read-only progress bars. */
  label?: string;
  /** Spoken in place of the raw number, for sliders whose value is not meaningful out of context. */
  valueText?: string;
  trackCSS?: Interpolation;
  rangeCSS?: Interpolation;
};

const rangeStyle: Interpolation = {
  backgroundColor: colors.white,
  borderRadius: radius.full,
  display: "block",
  height: numericPercentages[100],
  position: "absolute",
};
const rangeOnColorStyle: Interpolation = {
  backgroundColor: constantSemanticColors.onColorVeryHigh,
};
const rootStyle: Interpolation = {
  /** Radix offsets the thumb horizontally only; centring it vertically is the container's job. */
  alignItems: "center",
  display: "flex",
  height: spacing.s,
  position: "relative",
  width: numericPercentages[100],
};
const rootXLStyle: Interpolation = {
  height: spacing.m,
};
const smallRangeStyle: Interpolation = {
  minWidth: spacing.s,
};
const smallRootStyle: Interpolation = {
  width: numericPixels[240],
};
const thumbStyle: Interpolation = {
  borderRadius: radius.full,
  display: "block",
  height: numericPixels[0],
  width: numericPixels[0],
  ":focus-visible": {
    backgroundColor: colors.white,
    height: numericPixels[12],
    outline: `${stroke.regular} solid ${shades.shade60}`,
    width: numericPixels[12],
  },
};
const trackStyle: Interpolation = {
  backgroundColor: constantSemanticColors.onColorHigh,
  borderRadius: radius.full,
  display: "block",
  height: numericPercentages[100],
  position: "relative",
  width: numericPercentages[100],
  overflow: "hidden",
};
const disabledTrackStyle: Interpolation = {
  cursor: "default",
};
const dynamicColorStyle: Interpolation<string> = (color) => ({
  backgroundColor: color,
});

export const Slider = ({
  size = "large",
  value = 0,
  step = 1,
  defaultValue = 0,
  orientation = "horizontal",
  onColor,
  color,
  disabled,
  css,
  label,
  valueText,
  rangeCSS = {},
  trackCSS = {},
  ...props
}: SliderProps) => {
  const getColor = useEmotionColor();
  return (
    <Root
      value={[isNaN(value) ? 0 : value]}
      defaultValue={[defaultValue]}
      step={step}
      orientation={orientation}
      disabled={disabled}
      css={[
        rootStyle,
        size === "small" ? smallRootStyle : null,
        size === "xl" ? rootXLStyle : null,
        css,
      ]}
      {...props}
    >
      <Track css={[trackStyle, disabled ? disabledTrackStyle : null, trackCSS]}>
        <Range
          css={[
            rangeStyle,
            size === "small" ? smallRangeStyle : null,
            onColor ? rangeOnColorStyle : null,
            color ? dynamicColorStyle(getColor(color)) : null,
            rangeCSS,
          ]}
        />
      </Track>
      {label ? (
        <Thumb aria-label={label} aria-valuetext={valueText} css={thumbStyle} />
      ) : null}
    </Root>
  );
};
